import React, { useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { zindexRadixPopper } from "~/css/zindexes";
import { Tooltip, type TooltipProps } from "~/ui";
import Menu, { type MenuItemProps } from "~/ui/menu";

import type { MenuItemTooltip_useGetPropsForExternalWorkspaces_note$key } from "~/__generated__/MenuItemTooltip_useGetPropsForExternalWorkspaces_note.graphql";

import meetingHeaderStyles from "../MeetingHeader.module.css";

type TooltipPropsForExternalWorkspaces = {
	/**
	 * Supplying this prop implies that the note is from an external workspace
	 */
	externalWorkspaceName: string | null;
};

/**
 * Meant to be paired with `MenuItemToBeDisabledForExternalWorkspaces`.
 * Use this if you'd like to disable the menu item for external workspaces, and show a corresponding tooltip explaining
 * why.
 */
export function useGetPropsForExternalWorkspaces(
	noteKey: MenuItemTooltip_useGetPropsForExternalWorkspaces_note$key,
): TooltipPropsForExternalWorkspaces {
	const note = useFragment(
		graphql`
			fragment MenuItemTooltip_useGetPropsForExternalWorkspaces_note on Note {
				stream {
					isExternal
					account {
						name
					}
				}
			}
		`,
		noteKey,
	);

	return useMemo(
		() => ({
			externalWorkspaceName: note.stream.isExternal ? note.stream.account.name : null,
		}),
		[note.stream.isExternal, note.stream.account.name],
	);
}

export function MenuItemTooltip({ placement = "bottom", ...props }: TooltipProps) {
	return (
		<Tooltip
			className={meetingHeaderStyles.tooltipContent}
			placement={placement}
			maxWidth="12rem"
			zIndex={zindexRadixPopper + 1}
			{...props}
		>
			{props.children}
		</Tooltip>
	);
}

export function MenuItemToBeDisabledForExternalWorkspaces({
	externalWorkspaceName,
	disabled,
	children,
	...props
}: MenuItemProps & TooltipPropsForExternalWorkspaces) {
	return (
		<Menu.Item disabled={disabled || Boolean(externalWorkspaceName)} {...props}>
			{/* TODO: This tooltip currently doesn't work. I think it's because disabled menu items don't receive the pointer
			events */}
			<MenuItemTooltip
				disabled={!externalWorkspaceName}
				content={
					<div>
						You cannot do this.
						<br />
						Only <strong>{externalWorkspaceName}</strong> users can perform this action.
					</div>
				}
			>
				<div>{children}</div>
			</MenuItemTooltip>
		</Menu.Item>
	);
}
